import { useState } from 'react';
import { Play, ExternalLink, Clock, Eye, ThumbsUp } from 'lucide-react';
import type { YouTubeVideoData } from '../../types/index';
import { formatNumber } from '../../utils/formatNumber';

interface YouTubeEmbedProps {
  youtubeData: YouTubeVideoData;
  onPlay?: () => void;
}

export default function YouTubeEmbed({ youtubeData, onPlay }: YouTubeEmbedProps) {
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlay = () => {
    setIsPlaying(true);
    onPlay?.();
  };

  const openYouTube = () => {
    window.open(`https://youtube.com/watch?v=${youtubeData.videoId}`, '_blank');
  };

  return (
    <div className="relative bg-black rounded-lg overflow-hidden group">
      {/* Thumbnail */}
      <div className="relative aspect-video">
        <img
          src={youtubeData.thumbnail}
          alt={youtubeData.title}
          className="w-full h-full object-cover"
        />
        
        {/* Play overlay */}
        {!isPlaying && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 group-hover:bg-opacity-40 transition-all duration-200">
            <button
              onClick={handlePlay}
              className="w-16 h-16 bg-red-600 hover:bg-red-700 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
            >
              <Play className="h-6 w-6 text-white ml-1" fill="white" />
            </button>
          </div>
        )}

        {/* Duration badge */}
        <div className="absolute bottom-2 right-2 bg-black bg-opacity-80 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {youtubeData.duration}
        </div>

        {/* YouTube logo */}
        <div className="absolute top-2 left-2 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded">
          YouTube
        </div>
      </div>

      {/* Video info */}
      <div className="p-3 bg-gray-50 dark:bg-gray-800">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 line-clamp-2 mb-1">
          {youtubeData.title}
        </h3>
        
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
          <span className="font-medium">{youtubeData.channelTitle}</span>
          <button
            onClick={openYouTube}
            className="flex items-center gap-1 hover:text-red-600 dark:hover:text-red-400 transition-colors"
          >
            <ExternalLink className="h-3 w-3" />
            Watch
          </button>
        </div>

        {/* Stats */}
        {(youtubeData.viewCount || youtubeData.likeCount) && (
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
            {youtubeData.viewCount && (
              <div className="flex items-center gap-1">
                <Eye className="h-3 w-3" />
                {formatNumber(parseInt(youtubeData.viewCount))} views
              </div>
            )}
            {youtubeData.likeCount && (
              <div className="flex items-center gap-1">
                <ThumbsUp className="h-3 w-3" />
                {formatNumber(parseInt(youtubeData.likeCount))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Embedded player */}
      {isPlaying && (
        <div className="absolute inset-0 bg-black">
          <iframe
            src={`https://www.youtube.com/embed/${youtubeData.videoId}?autoplay=1`}
            title={youtubeData.title}
            className="w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      )}
    </div>
  );
}
